import { ButtonV3, FormControl } from '@jouzen/ecom-components';
import { EmailValidator } from 'commons-validator-js';
import { useFormik } from 'formik';
import noop from 'lodash/noop';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { EventType } from '@/analytics/types';

import type { OutOfStockModalEventProperties } from './types';

interface FormValues {
  outOfStockEmail: string;
}

export interface EmailFormProps {
  readonly eventProperties: OutOfStockModalEventProperties;
  readonly onSubmit?: () => void;
}

const EmailForm = ({ eventProperties, onSubmit = noop }: EmailFormProps) => {
  const t = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleEmailNotifyMeAnalytics = async (
    values: FormValues,
  ): Promise<void> => {
    setIsSubmitting(true);
    const { productType: _productType, ...payload } = eventProperties;
    const email = values.outOfStockEmail.toLowerCase();
    await window.ouraAnalytics.identify({ email });
    await window.ouraAnalytics.track(EventType.UserEmailCaptured);
    await window.ouraAnalytics.track(EventType.EmailOOSNotificationSignup, {
      ...payload,
      email,
    });
    setIsSubmitting(false);
  };

  const formik = useFormik({
    initialValues: {
      outOfStockEmail: '',
    },
    onSubmit: async (values) => {
      await handleEmailNotifyMeAnalytics(values);
      formik.resetForm();
      onSubmit();
    },
    validate: (values) => {
      const validator = new EmailValidator();
      const sanitized = values.outOfStockEmail.trim().toLowerCase();

      if (!sanitized) {
        return {
          outOfStockEmail: t('checkout_form_emailrequired'),
        };
      }

      if (!validator.isValid(sanitized)) {
        return {
          outOfStockEmail: t('checkout_form_emailformat'),
        };
      }

      return {};
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  return (
    <form className="w-full" noValidate onSubmit={formik.handleSubmit}>
      <FormControl
        className="w-full"
        control="TextInput"
        label={t('inventory_notify_module_form_title')}
        errorMessage={formik.errors.outOfStockEmail}
        maxLength={80}
        name="outOfStockEmail"
        onChange={formik.handleChange}
        value={formik.values.outOfStockEmail}
      />
      <ButtonV3
        className="w-full font-bold"
        data-cy="out-of-stock-modal-submit"
        disabled={isSubmitting}
        loading={isSubmitting}
        size="large"
        type="submit"
        variant="secondary-dark"
      >
        {t('notify_me_modal_submit')}
      </ButtonV3>
    </form>
  );
};

export default EmailForm;
